import { useEffect, useState } from "react";
import posthog from "posthog-js";

import { VARIANT_CONTROL, VARIANT_HOLDOUT_PREFIX } from "./experimentKeys";

// useExperimentVariant resolves one PostHog experiment variant for the current user.
//
// Returns { ready, variant }. Callers MUST branch on `ready` before rendering anything
// that differs between variants: PostHog loads flags asynchronously, so rendering the
// control experience and then swapping it is not a flicker -- it shows the customer one
// thing and then measures another.
//
// `eligible` gates BOTH the render and the exposure. getFeatureFlag records a
// $feature_flag_called event, which is what PostHog counts as the exposure, so calling
// it for a user who could never have seen the variant pollutes the experiment
// population and dilutes the result. Pass false and the hook never asks.
//
// Users in the global holdout come back as "holdout-<id>" rather than "control".
// Normalising them to control is deliberate: the holdout is a baseline, so it belongs
// with the baseline behaviour, and treating it as its own value would split the control
// group in two.
export function useExperimentVariant(
  flagKey,
  { eligible = true, exposureProperties } = {},
) {
  const [state, setState] = useState({
    eligible,
    ready: !eligible,
    variant: VARIANT_CONTROL,
  });

  useEffect(() => {
    if (!eligible) {
      setState({ eligible, ready: true, variant: VARIANT_CONTROL });
      return undefined;
    }

    // Attach segmenting context to the exposure event BEFORE asking for flags.
    //
    // PostHog's exposure event is $feature_flag_called, which it generates itself --
    // there is no per-call properties argument -- so the only way to get a dimension
    // onto it is a super property registered first. Without this, an experiment can
    // only ever be read in aggregate: the metric event may carry the dimension, but
    // filtering the metric narrows the NUMERATOR while the denominator stays every
    // exposure, which yields a rate that means nothing.
    //
    // register_for_session, not register: session-scoped, so a value describing the
    // visit someone is in now does not persist in localStorage and stick itself onto
    // unrelated events weeks later.
    //
    // Deliberately NOT setPersonPropertiesForFlags, which would feed these values
    // into flag EVALUATION and could change how someone is bucketed. This is
    // reporting context only -- it must not touch assignment.
    if (exposureProperties) {
      posthog.register_for_session(exposureProperties);
    }

    // Automatic flag fetching is disabled at init (see posthogInit) so that a screen
    // with no experiment never costs a flag request. Ask for them here, where one is
    // actually needed. reloadFeatureFlags is a no-op if they are already loaded.
    posthog.reloadFeatureFlags();

    // onFeatureFlags fires once flags are available and again if they reload. It
    // returns its own unsubscribe, so the effect cleans itself up.
    return posthog.onFeatureFlags(() => {
      const resolved = posthog.getFeatureFlag(flagKey);
      const variant =
        typeof resolved === "string" &&
        !resolved.startsWith(VARIANT_HOLDOUT_PREFIX)
          ? resolved
          : VARIANT_CONTROL;

      setState({ eligible, ready: true, variant });
    });
  }, [flagKey, eligible, JSON.stringify(exposureProperties)]);

  // An answer belongs to the eligibility it was computed under. When `eligible`
  // changes, the state still holds the previous answer until the effect above
  // runs, and a caller that read it as settled would freeze an "ineligible, so
  // control" answer as this user's variant while PostHog records the real one.
  if (state.eligible !== eligible) {
    return { ready: !eligible, variant: VARIANT_CONTROL };
  }
  return { ready: state.ready, variant: state.variant };
}
